<template>
  <div
    id="dashboard"
    class="pa-4"
  >
    <section class="dashboard__players mb-6">
      <v-row>
        <v-col>
          <v-row
            v-if="hasItlModule"
            class="mb-4"
          >
            <v-col>
              <stat-card
                :loading="teamStats.loading"
                :title="$t('card_title.team_dl')"
                :value-text="(teamStats.allStats[teamStats.allStats.length - 2] ? (teamStats.allStats[teamStats.allStats.length - 2].stats.DL || '0') : '0') + ' / ' + (teamStats.data.DL || '0')"
                class="fill-height"
              />
            </v-col>
            <v-col>
              <stat-card
                :loading="teamStats.loading"
                :title="$t('card_title.team_cl')"
                :value="teamStats.data.CL"
                class="fill-height"
              />
            </v-col>
            <v-col>
              <stat-card
                :loading="teamStats.loading"
                :title="$t('card_title.acwr')"
                :value="teamStats.data.ACWR"
                class="fill-height"
                :max="2"
                :color="getAcwrColorFromValue(teamStats.data.ACWR)"
              />
            </v-col>
            <v-col
              v-if="showWellnessData"
              cols="3"
            >
              <stat-card
                :loading="teamStats.loading"
                :title="$t('card_title.player_wellness')"
                :value="teamStats.data.player_wellness"
                :value-text="teamStats.data.player_wellness ? teamStats.data.player_wellness.toFixed(0) + '%' : undefined"
                class="fill-height"
                :max="100"
                :color="getColorFromValue(teamStats.data.player_wellness, 100)"
              />
            </v-col>
          </v-row>

          <v-card class="rounded-lg">
            <v-card-title class="px-6 py-4">
              <v-row
                no-gutters
                align="center"
              >
                <v-col>
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t('card_title.player_status_overview') }}
                  </div>
                </v-col>
                <v-col
                  v-if="!isIndividualSport"
                  cols="auto"
                >
                  <v-select
                    v-model="playerPositions.selectedId"
                    solo
                    dense
                    :label="$t('table_headers.player_overview.position')"
                    hide-details
                    clearable
                    item-value="id"
                    item-text="name"
                    :items="playerPositions.data"
                    style="width: 200px"
                    @change="fetchPlayers()"
                  />
                </v-col>
                <v-col
                  cols="auto"
                  class="pl-2"
                >
                  <v-select
                    v-model="playerStatuses.selectedId"
                    solo
                    dense
                    :label="$t('table_headers.player_overview.status')"
                    hide-details
                    clearable
                    :items="playerStatuses.data"
                    item-value="id"
                    item-text="name"
                    style="width: 200px"
                    @change="fetchPlayers()"
                  />
                </v-col>
              </v-row>
            </v-card-title>
            <v-card-text class="pa-0">
              <players-list-table
                :players="players.data"
                :loading="players.loading"
                :max-values="players.maxValues"
                @tableRowClick="onPlayerRowClicked"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </section>

    <section
      v-if="hasItlModule"
      class="dashboard__charts pt-5 pb-16"
    >
      <line-chart
        :raw-data="teamStats.allStats"
        x-value="date"
        y-value-prefix="stats."
        :chart-value-keys="[{ key: 'DL', label: $t('chart_legends.daily_load'), type: 'bar' }, { key: 'CL', label: $t('chart_legends.chronic_load'), type: 'area' }, { key: 'ACWR', label: $t('table_headers.player_overview.acwr'), type: 'line' }]"
        class="mb-5"
        :yaxis="[ { title: { text: $t('chart_legends.load')} }, { title: { text: '' }, labels: { show:false }}, { title: { text: $t('card_title.acwr')}, opposite: true}]"
        :stroke="{ width: [0, 1, 5], curve: 'smooth' }"
        :fill="{ type: ['solid', 'gradient', 'solid'], opacity: [1, 0.25, 1], gradient: { inverseColors: false, shade: 'light', type: 'vertical', opacityFrom: 0.85, opacityTo: 0.55 } }"
        :markers="{colors: ['#00E396','#008FFB', '#FEB019']}"
      >
        <template v-slot:header>
          <v-card-title class="pa-6">
            <div class="subtitle-2 grey--text text--grey-100">
              {{ $t('card_title.main_team_indicators') }}
            </div>
          </v-card-title>
        </template>
      </line-chart>
    </section>
  </div>
</template>

<script>
import PlayersListTable from '@/components/players/PlayersListTable'
import { PlayerApiService, PlayerPositionApiService, PlayerStatusApiService, TeamApiService } from '@/data/services'
import debounce from 'lodash/debounce'
import dayjs from 'dayjs'
import LineChart from '@/components/charts/LineChart'
import StatCard from '@/components/StatCard'
import { getColorForValue, getColorForValueWithDefinition, processMaxValues, toCamelCase } from '@/utils/helpers'
import { acwrColorsDefinition } from '@/data/models/Stats'
import { EventBus } from '@/plugins/bus'

export default {
  name: 'DashboardView',
  // eslint-disable-next-line vue/no-unused-components
  components: {
    StatCard,
    LineChart,
    PlayersListTable
  },
  data: () => ({
    players: {
      data: [],
      loading: false,
      pagination: {
        current_page: null,
        last_page: null,
        per_page: 20
      },
      maxValues: {}
    },
    events: {
      data: [],
      loading: false
    },
    teamStats: {
      loading: false,
      data: {},
      allStats: []
    },
    playerStatuses: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    playerPositions: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    }
  }),
  computed: {
    currentTeamId() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]?.id
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    isIndividualSport() {
      return this.user.sport.is_individual
    },
    showWellnessData() {
      return !this.user.organisation_settings.has_short_wellness_feedback
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module || false
    },
    hasNoPlayers() {
      return this.user.organisation_settings.has_no_players || false
    }
  },
  watch: {
    currentTeamId() {
      this.fetchPlayers()
      this.fetchTeamStats()
    }
  },
  created() {
    if (this.hasNoPlayers) {
      this.$router.push({ name: 'events' })
    }

    this.players.loading = true

    this.fetchPlayers()
    // this.fetchEvents()

    this.fetchPlayerPositions()
    this.fetchPlayerStatuses()

    const today = dayjs()
    let startDate = dayjs().subtract(7, 'day')

    const days = []

    while (startDate.isBefore(today)) {
      days.push(startDate.format('YYYY-MM-DD'))
      startDate = startDate.add(1, 'day')
    }

    if (this.currentTeamId) {
      this.fetchTeamStats()
    }

    EventBus.$on(this.$events.INJURY_CREATED, () => {
      this.fetchPlayers()
    })
    EventBus.$on(this.$events.INJURY_UPDATED, () => {
      this.fetchPlayers()
    })
    EventBus.$on(this.$events.INJURY_DELETED, () => {
      this.fetchPlayers()
    })
  },
  methods: {
    fetchPlayers: debounce(async function () {
      if (!this.currentTeamId === 'all' || !this.currentTeamId) return

      try {
        this.players.loading = true

        const queryParams = {
          team_id: this.currentTeamId === 'all' ? undefined : this.currentTeamId,
          player_position_id: this.playerPositions.selectedId ?? undefined,
          player_status_id: this.playerStatuses.selectedId ?? undefined,
          no_event_stats: true
        }

        const response = await PlayerApiService.getAll(queryParams)

        const players = response.data

        this.players.maxValues = processMaxValues(players.map(player => {
          return player.latest_stats || {}
        }))

        this.players.data = players
      } catch (e) {
        this.players.data = []
        throw e
      } finally {
        this.players.loading = false
      }
    }, 50),
    fetchEvents: debounce(async function () {
      try {
        this.events.loading = true

        // const response = await EventApiService.fetchList()

        // this.events.data = response.data.events
      } catch (e) {
        // todo
      } finally {
        this.events.loading = false
      }
    }, 500),
    onPlayerRowClicked(playerId) {
      this.$router.push({
        name: this.$routeNames.PLAYER_DETAILS,
        params: { id: playerId }
      })
    },
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll()

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerStatuses.loading = false
      }
    },
    async fetchTeamStats() {
      if (this.currentTeamId === 'all' || !this.currentTeamId) return

      this.teamStats.loading = true

      try {
        this.teamStats.data = await TeamApiService.getLatestStats(this.currentTeamId)

        this.teamStats.allStats = await TeamApiService.getStats(this.currentTeamId, dayjs().subtract(14, 'days').toISOString(), dayjs().toISOString())
      } catch (e) {
        this.$store.dispatch('alert', {
          text: this.$t('alert.pulling_team_stats.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.teamStats.loading = false
      }
    },
    getAcwrColorFromValue(value) {
      return getColorForValueWithDefinition(value, acwrColorsDefinition)
    },
    getColorFromValue(value, max) {
      return getColorForValue(value, max)
    }
  }
}
</script>
